import { Injectable } from '@angular/core';
import { ModelPage, ModelService, PagedModelService } from '@app/shared/model-system';
import { User } from '@app/shared/models/user/user';
import { ApiService } from '@app/shared/services/api.service';
import { compact, pullAllWith } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/** User service using model system */
@Injectable()
export class UserService extends ModelService<User> implements PagedModelService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public Type = User;
  private readonly baseURI = '/platform/api/v2/users';

  constructor(public api: ApiService) {
    super();
  }

  public async getPage({
    pageNumber,
    pageSize,
    searchTerm = '',
    searchFields = ['name', 'email', 'title', 'department'],
    state = 'active',
    query = []
  }: IUserServiceGetPageOptions): Promise<ModelPage<User>> {
    const body: IUserServiceSearchBody = {
      pageSize,
      pageNumber,
      types: ['users'],
      query: [...query],
      sortOrder: 'ASC',
      sortBy: 'name'
    };

    if (state === 'active_inactive') {
      body.query.push({
        type: 'EXACT',
        fields: ['state'],
        values: ['active', 'inactive']
      });
    } else {
      body.query.push({
        type: 'EXACT',
        fields: ['state'],
        value: state
      });
    }

    compact(searchTerm.replace(/[,;]/, ' ').split(/\s/) || []).forEach((term: string) => {
      let value = term.trim();
      if (!value.length) {
        return;
      }
      if (value === '*') {
        return;
      }
      body.query.push({
        type: 'QUERY_STRING',
        fields: searchFields,
        value: value.toLowerCase()
      });
    });

    let response = await this.api
      .post(`/platform/api/v2/search${state === 'active' ? '?profile=false' : ''}`, body)
      .toPromise();
    let models = response.results.map((entity) => this.create(entity));

    return {
      entities: models,
      pageNumber,
      pageSize,
      total: response.total
    };
  }

  public async get(id: string): Promise<User> {
    return this.api
      .get(`/platform/api/v2/users/${id}`)
      .pipe(map((data) => this.create(data)))
      .toPromise();
  }

  public async saveUser(model: User, verify: boolean = false): Promise<boolean> {
    if (!model.isModified() && !model.isNew()) {
      return Promise.resolve(true);
    }
    try {
      let data;
      if (model.isNew()) {
        data = await this.api.post(this.baseURI, model.toObject()).toPromise();
      } else {
        let user = await this.get(model.id);
        model.version = user.version;
        data = await this.api.patch(`${this.baseURI}/${model.id}`, model.toObject()).toPromise();
      }
      this.update(model, data);

      return Promise.resolve(true);
    } catch (error) {
      throw error;
    }
  }

  /**
   * find address, if exists, in user.addresses by address.type = detail.type
   * if exists, replace address.address = number
   * based on detail { type, address }
   */
  public async assignDID(user: User, number: string, detail: any): Promise<boolean> {
    let existing = user.addresses
      .filter((a) => a.mediaType === detail.mediaType)
      .find((a) => a.type === detail.type);
    if (existing) {
      existing.address = number;
    } else {
      detail.address = number;
      user.addresses.push(detail);
    }
    user.markModified('addresses');

    return this.saveUser(user);
  }

  /**
   * Unassign a single number from a user
   */
  public async unassignDID(userID: string, number: string): Promise<boolean> {
    let user = await this.get(userID);
    let addresses = user.addresses;
    for (let i = 0; i < addresses.length; i++) {
      if (addresses[i].address === number) {
        addresses.splice(i, 1);
      }
    }

    user.markModified('addresses');

    return this.saveUser(user);
  }

  /**
   * Written for bulk unassigning DID numbers when the user selects multiple on the list, previously the delete
   * many functionality called unassignDID in parallel causing version errors, use this to remove multiple numbers from
   * a user in a single call.
   */
  public async bulkUnassignDID(userID: string, numbers: string[]): Promise<boolean> {
    let user = await this.get(userID);

    pullAllWith(user.addresses, numbers, (arrVal, oVal) => arrVal.address === oVal);
    user.markModified('addresses');

    return this.saveUser(user);
  }

  public searchUsersWithoutTeamForTeamAssign(
    pageNumber: number,
    pageSize: number,
    name: string,
    enforcePermissions: boolean = false,
    divisionId: string
  ): Observable<ModelPage<User>> {
    let body: IUserServiceSearchBody = {
      pageNumber,
      pageSize,
      enforcePermissions,
      sortBy: 'name',
      sortOrder: 'ASC',
      expand: ['team'],
      query: [
        {
          type: 'REQUIRED_FIELDS',
          fields: ['teamId'],
          operator: 'NOT'
        },
        {
          type: 'EXACT',
          fields: ['divisionId'],
          value: divisionId
        },
        {
          type: 'EXACT',
          fields: ['state'],
          value: 'active',
          operator: 'AND'
        }
      ]
    };
    if (name) {
      body.query.push({
        type: 'QUERY_STRING',
        fields: ['name'],
        value: name
      });
    }

    return this.api.post(`/platform/api/v2/users/search/teams/assign`, body).pipe(
      map((data) => ({
        entities: data.results ? data.results.map((entity) => this.create(entity)) : [],
        total: data.total,
        pageNumber,
        pageSize
      }))
    );
  }
}

interface IUserServiceGetPageOptions {
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
  searchTerms?: string[];
  searchFields?: UserField[];
  state?: string;
  query?: IUserQuery[];
}

interface IUserServiceSearchBody {
  pageNumber: number;
  pageSize: number;
  enforcePermissions?: boolean;
  types?: string[];
  sortOrder?: string;
  sortBy?: string;
  expand?: string[];
  query?: IUserQuery[];
}

interface IUserQuery {
  type?: 'EXACT' | 'QUERY_STRING' | 'CONTAINS' | 'REQUIRED_FIELDS';
  fields?: UserField[];
  operator?: string;
  value?: string;
  values?: string[];
}

type UserField = 'name' | 'email' | 'title' | 'department' | 'state' | 'teamId' | 'divisionId';
